import type { Patient, PatientMedication } from '../types/patient.types';

// Field rules for the patient detail panel's inline correction (SHCT-737, PRD 02 v1.20 §3.6).
//
// Values come from [공통] 00_입력검증표준 (확정 2026-07-10), which PRD 02 §4.2 defers to and which
// overrides any older per-feature text by requirement precedence. They are stated here rather than
// imported from `PatientRegistrationModal`, which holds its own copies: unifying the two is a change to
// a live registration screen with no functional gain, so it is left to a ticket of its own.

/** 환자명 ([공통] 00 §2.5): 한글 2~20자. */
export const PATIENT_NAME_REGEX = /^[가-힣]{2,20}$/;

/** The same rule's upper bound, for the editor's shared length check. */
export const PATIENT_NAME_MAX = 20;

/**
 * 등록번호(MRN) ([공통] 00 §2.7, 확정 2026-07-10): 영문·숫자·'-' 최대 20자.
 *
 * Deliberately NOT the 8-digit `YYYYXXXX` form PRD 02 describes: real hospital-issued numbers do not all
 * follow it (QA data carries `a202611`), and enforcing the documented shape would make a genuine MRN
 * unenterable. `PatientRegistrationModal` holds the identical rule for the registration form, and
 * admin-api's own edit path accepts exactly this pattern.
 */
export const MRN_REGEX = /^[A-Za-z0-9-]{1,20}$/;
export const MRN_MAX = 20;

/** 보호자명 (§8 부록 → §3 NAME 클래스): 1~50자, 이모지·줄바꿈 불허. */
export const GUARDIAN_NAME_MIN = 1;
export const GUARDIAN_NAME_MAX = 50;

/**
 * 처방약 · 복약주기 — [공통] 00 §8 부록 assigns both the NAME class (1~50자, single line, 이모지 불허).
 * PRD 02 defines no length of its own for either field.
 */
export const MEDICATION_MAX = 50;

/**
 * 거주지 · 상세 주소 (SHCT-915, PRD 02 v1.23 §3.6).
 *
 * 상세 주소 = 100자 is the canonical rule — [공통] 00_입력검증표준 §5-⑦, which the customer named in the
 * ticket and which the patient app already enforces on the same value. 거주지 has no length in either the
 * 입력검증표준 or PRD 02: the value is a road-name address the patient picks from the national service, so
 * nothing here authors it. 200 is admin-api's own column bound, mirrored so the editor can say which
 * limit was passed instead of letting a save fail with a generic error.
 */
export const RESIDENCE_MAX = 200;
export const RESIDENCE_DETAIL_MAX = 100;

/** `<input type="date">` wants YYYY-MM-DD; the panel's display formatter wants the same source value. */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

/** Today in KST as YYYY-MM-DD — the 퇴원일 picker's max, since §4.2 allows 오늘 이전 날짜만. */
export function todayInKst(): string {
  const kstMs = Date.now() + 9 * 60 * 60 * 1000;
  return new Date(kstMs).toISOString().slice(0, 10);
}

/**
 * The 처방약 rows to display and edit, from whichever shape the server sent (SHCT-738 · SHCT-737).
 *
 * `medications` is the real list (`pt_patient_medication`, V96). When the server does not send it — today's
 * admin-api — the superseded `prescriptionDrug`/`medicationCycle` pair on `pt_patient` is read instead, so
 * a patient whose single drug is still stored there keeps showing it. A pair with no 약품명 yields no row:
 * a 복약주기 on its own names nothing.
 */
export function medicationRows(
  patient: Pick<Patient, 'medications' | 'prescriptionDrug' | 'medicationCycle'>,
): PatientMedication[] {
  if (Array.isArray(patient.medications)) return patient.medications;
  const name = patient.prescriptionDrug?.trim() ?? '';
  if (!name) return [];
  return [{ name, freq: patient.medicationCycle?.trim() ?? '' }];
}

/**
 * The rows the 처방약 editor opens with — the stored ones, or a single blank row so there is something to
 * type into for a patient who has no prescription yet.
 */
export function initialMedicationRows(
  patient: Pick<Patient, 'medications' | 'prescriptionDrug' | 'medicationCycle'>,
): PatientMedication[] {
  const rows = medicationRows(patient);
  return rows.length > 0 ? rows.map((r) => ({ ...r })) : [{ name: '', freq: '' }];
}
